import React, { createContext, useState, useCallback, useContext, useEffect } from 'react';
import { getOrder } from '../services/experienceOrderService';
import { Order } from '../services/booking-types';

interface CheckoutContextType {
  order: Order | null;
  loading: boolean;
  error: Error | null;
  fetchOrder: () => Promise<Order | void>;
}

export const CheckoutContext = createContext<CheckoutContextType>({
  order: null,
  loading: false,
  error: null,
  fetchOrder: async () => {},
});

export const CheckoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const orderData = await getOrder();
      setOrder(orderData);
      return orderData;
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e : new Error('Failed to fetch order'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  return (
    <CheckoutContext.Provider
      value={{
        order,
        loading,
        error,
        fetchOrder
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

// Custom hook to use the checkout context
export const useCheckoutContext = () => {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error('useCheckoutContext must be used within a CheckoutProvider');
  }
  return context;
};
